import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useLocation,
  useSearchParams,
  useNavigate,
  useRouteError,
} from 'react-router-dom'

import { ResidentAppError } from '../../../common/error'
import { ResidentAppErrorCode } from '../../../common/types'
import { LoginViewError } from './LoginViewTypes'

import routePath from 'router/routePath'
import { useResetRouteState } from 'hooks/useResetRouteState'

export const useLoginViewErrorHandler = (viewHasError?: boolean) => {
  const [t] = useTranslation(['login', 'errors', 'registration'])
  const [error, setError] = React.useState<string | null>(null)
  const { state } = useLocation()
  const { resetRouteState } = useResetRouteState()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const routeError = useRouteError()

  // Handle view errors
  useEffect(() => {
    if (!viewHasError) return

    if (routeError instanceof ResidentAppError) {
      switch (routeError.errorCode) {
        case ResidentAppErrorCode.INVALID_CREDENTIALS:
          setError(t('errors:auth.invalidCredentials'))
          break
        default:
          setError(t('errors:other.internalError'))
      }
    } else {
      setError(t('errors:other.internalError'))
    }
  }, [viewHasError, routeError, t])

  // Handle backend errors
  useEffect(() => {
    const err = params.get('error')

    if (!err) return

    if (err === LoginViewError.NO_TENANT_FOUND) {
      navigate(routePath.registerFtnCallbackError, {
        replace: true,
      })
      return
    }

    const email = params.get('email') || ''
    const correctUrl = params.get('correctUrl') || ''

    // Update the view with the backend error message
    navigate(routePath.logout, {
      state: { error: err, email, correctUrl },
      replace: true,
    })
  }, [navigate, params, t])

  // Handle frontend errors
  useEffect(() => {
    if (!state?.error) return

    switch (state?.error) {
      case LoginViewError.USER_ALREADY_REGISTERED:
        setError(
          t('login:errors.userAlreadyRegistered', { email: state.email })
        )
        break
      case LoginViewError.REGISTRATION_LINK_FETCH_FAILED:
        setError(t('errors:other.registrationIsOutOfOrder'))
        break
      case LoginViewError.REGISTRATION_CANCELLED:
        setError(t('registration:registrationCancelled'))
        break
      case LoginViewError.REGISTRATION_FAILED:
        setError(t('registration:registrationFailed'))
        break
      case LoginViewError.WRONG_DOMAIN: {
        const correctUrl =
          typeof state.correctUrl === 'string' ? state.correctUrl : ''
        const isDifferentOrigin = (() => {
          try {
            return new URL(correctUrl).origin !== window.location.origin
          } catch {
            return false
          }
        })()
        if (isDifferentOrigin) {
          setError(
            t('registration:registrationWrongDomain', { url: correctUrl })
          )
        } else {
          setError(t('registration:registrationFailed'))
        }
        break
      }
      case LoginViewError.LOGIN_EXPIRED:
        setError(t('login:errors.loginExpired'))
        break
      default:
        setError(t('errors:other.internalError'))
    }

    // Reset the routes error state after handling it
    resetRouteState()
  }, [navigate, resetRouteState, state, t])

  return { error }
}
